
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Tag, Edit } from "lucide-react";
import { getYouTubeVideoId, getYouTubeEmbedUrl } from "@/utils/youtube";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface VideoCardProps {
  id: string;
  title: string;
  description?: string;
  youtubeUrl: string;
  userId: string;
  category?: string | null;
  onDelete: (videoId: string, userId: string) => void;
  onCategoryUpdate?: () => void;
}

const categories = [
  "Driving",
  "Iron Play",
  "Putting",
  "Bunker",
  "Wedges",
  "Strategy",
  "Mental",
  "Rules",
  "Other"
];

export const VideoCard = ({
  id,
  title,
  description,
  youtubeUrl,
  userId,
  category,
  onDelete,
  onCategoryUpdate,
}: VideoCardProps) => {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const videoId = getYouTubeVideoId(youtubeUrl);
  if (!videoId) return null;

  const isShort = youtubeUrl.includes('/shorts/');

  const handleCategoryChange = async (newCategory: string) => {
    try {
      setIsUpdating(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Erreur",
          description: "Vous devez être connecté pour modifier une vidéo",
          variant: "destructive",
        });
        return;
      }

      if (user.id !== userId) {
        toast({
          title: "Erreur",
          description: "Vous ne pouvez modifier que vos propres vidéos",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from("youtube_videos")
        .update({ category: newCategory })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "La catégorie a été mise à jour avec succès",
      });

      if (onCategoryUpdate) {
        onCategoryUpdate();
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour de la catégorie",
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex-1">
          <CardTitle className="text-golf-700">{title}</CardTitle>
          {category && (
            <div className="mt-2 flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800 hover:bg-green-200 flex items-center gap-1 w-fit">
                <Tag className="h-3 w-3" />
                {category}
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={isUpdating}>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 text-gray-500 hover:text-gray-700"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {categories.map((cat) => (
                    <DropdownMenuItem
                      key={cat}
                      onClick={() => handleCategoryChange(cat)}
                      className={cat === category ? "bg-green-50 font-medium" : ""}
                    >
                      {cat}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {!category && (
            <div className="mt-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={isUpdating}>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-gray-500 text-xs flex items-center gap-1"
                  >
                    <Edit className="h-3 w-3" />
                    Ajouter une catégorie
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {categories.map((cat) => (
                    <DropdownMenuItem
                      key={cat}
                      onClick={() => handleCategoryChange(cat)}
                    >
                      {cat}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(id, userId)}
          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-100"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className={isShort ? "aspect-[9/16] max-w-sm mx-auto" : "aspect-video"}>
          <iframe
            src={getYouTubeEmbedUrl(videoId)}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full rounded-md"
          />
        </div>
        {description && (
          <p className="text-gray-600 mt-4">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};
